import {
  itunesArtistAlbumsUrl,
  itunesSearchUrl,
  parseArtistCovers,
  parseItunesArtists,
  parseItunesTracks,
  type CatalogArtist,
  type CatalogTrack,
} from '@mixtape/shared';
import { api, ApiClientError, call } from './api';

export type Track = Awaited<ReturnType<typeof addItunesTrack>>;

/**
 * After a direct iTunes failure (CORS, 403/429 rate limit, network) we use our proxy for a while,
 * so a throttled browser does not keep hammering Apple.
 */
const DIRECT_COOLDOWN_MS = 60_000;
let directBlockedUntil = 0;

async function direct<T>(
  url: string,
  parse: (json: unknown) => T,
  signal: AbortSignal | undefined,
): Promise<T> {
  const res = await fetch(url, {
    signal: signal ?? null,
    mode: 'cors',
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
  });
  if (!res.ok) throw new Error(`itunes ${res.status}`);
  return parse(await res.json());
}

function isAbort(err: unknown) {
  return err instanceof DOMException && err.name === 'AbortError';
}

/**
 * Song search: the browser asks iTunes directly (each user spends their own rate-limit quota);
 * if that fails we fall back to our cached, rate-limited proxy.
 */
export async function searchSongs(
  term: string,
  country: string,
  signal?: AbortSignal,
): Promise<CatalogTrack[]> {
  if (Date.now() > directBlockedUntil) {
    try {
      return await direct(itunesSearchUrl(term, 'song', country), parseItunesTracks, signal);
    } catch (err) {
      if (isAbort(err)) throw err;
      directBlockedUntil = Date.now() + DIRECT_COOLDOWN_MS;
    }
  }
  const res = await call(
    api.api.v1.music.search.$get(
      { query: { q: term, type: 'song' } },
      { init: { signal: signal ?? null } },
    ),
  );
  return res.tracks;
}

/**
 * Adds an album cover per artist (Apple's public API has no artist photos). One extra request;
 * names matter more than pictures, so if it fails the artists come back without them.
 */
async function withCovers(
  artists: CatalogArtist[],
  country: string,
  signal: AbortSignal | undefined,
): Promise<CatalogArtist[]> {
  if (artists.length === 0) return artists;
  try {
    const covers = await direct(
      itunesArtistAlbumsUrl(
        artists.map((a) => a.itunesArtistId),
        country,
      ),
      parseArtistCovers,
      signal,
    );
    return artists.map((a) => ({ ...a, imageUrl: covers.get(a.itunesArtistId) ?? null }));
  } catch (err) {
    if (isAbort(err)) throw err;
    return artists;
  }
}

export async function searchArtists(
  term: string,
  country: string,
  signal?: AbortSignal,
): Promise<CatalogArtist[]> {
  if (Date.now() > directBlockedUntil) {
    try {
      const artists = await direct(
        itunesSearchUrl(term, 'musicArtist', country),
        parseItunesArtists,
        signal,
      );
      return await withCovers(artists, country, signal);
    } catch (err) {
      if (isAbort(err)) throw err;
      directBlockedUntil = Date.now() + DIRECT_COOLDOWN_MS;
    }
  }
  const res = await call(
    api.api.v1.music.search.$get(
      { query: { q: term, type: 'artist' } },
      { init: { signal: signal ?? null } },
    ),
  );
  return res.artists;
}

export function addItunesTrack(itunesTrackId: number) {
  return call(api.api.v1.music.tracks.$post({ json: { itunesTrackId } }));
}

export function addAppleLink(url: string) {
  return call(api.api.v1.music.tracks['from-apple'].$post({ json: { url } }));
}

export function resolveSpotify(url: string) {
  return call(api.api.v1.music.spotify.resolve.$post({ json: { url } }));
}

export function addSpotifyOnly(url: string, artist: string) {
  return call(api.api.v1.music.tracks['spotify-only'].$post({ json: { url, artist } }));
}

export function linkSpotify(trackId: string, url: string) {
  return call(
    api.api.v1.music.tracks[':id'].spotify.$post({ param: { id: trackId }, json: { url } }),
  );
}

export type Tape = Awaited<ReturnType<typeof getTape>>;
export type TapeSlot = Tape['sideA'][number];

export function getTape() {
  return call(api.api.v1.me.tracks.$get());
}

export function saveTape(tape: {
  sideA: { trackId: string; spotifyTrackId: string | null }[];
  sideB: { trackId: string; spotifyTrackId: string | null }[];
}) {
  return call(api.api.v1.me.tracks.$put({ json: tape }));
}

export type Vibe = Awaited<ReturnType<typeof getVibe>>;

export function getVibe() {
  return call(api.api.v1.me.vibe.$get());
}

export function saveVibe(input: {
  genres: Vibe['genres'];
  itunesArtistIds: number[];
  answers: Vibe['answers'];
}) {
  return call(api.api.v1.me.vibe.$put({ json: input }));
}

/**
 * Retry policy for catalog searches: Apple's rate limit ("the tape is rewinding") and network
 * blips usually clear within seconds, so retry a few times with growing delays.
 */
export const searchRetry = {
  retry: (failureCount: number, error: unknown) =>
    failureCount < 3 &&
    error instanceof ApiClientError &&
    (error.code === 'UPSTREAM_BUSY' || error.code === 'NETWORK' || error.code === 'UPSTREAM_ERROR'),
  retryDelay: (attempt: number) => 2000 * 2 ** attempt,
};
